/**
 * EditMemberPermissionsModal Component
 *
 * Modal for editing member permissions
 */

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Workspace, WorkspaceMember, WorkspacePermissions } from '../../../types/workspace';
import { useWorkspace } from '../../../contexts/WorkspaceContext';

interface EditMemberPermissionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace;
  member: WorkspaceMember;
  onSuccess?: () => void;
}

export const EditMemberPermissionsModal: React.FC<EditMemberPermissionsModalProps> = ({
  isOpen,
  onClose,
  workspace,
  member,
  onSuccess,
}) => {
  const { updateWorkspaceMember } = useWorkspace();
  const [permissions, setPermissions] = useState<WorkspacePermissions>(member.permissions);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = (key: keyof WorkspacePermissions) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: prev[key] === 1 ? 0 : 1,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      await updateWorkspaceMember(workspace.id, member.user_id, { permissions });
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar permissões');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setPermissions(member.permissions);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  const permissionList = [
    {
      key: 'can_view_analytics' as keyof WorkspacePermissions,
      label: 'Ver Analytics',
      description: 'Visualizar analytics do workspace',
    },
    {
      key: 'can_manage_agents' as keyof WorkspacePermissions,
      label: 'Gerenciar Agentes',
      description: 'Criar/editar/deletar agentes, bases de conhecimento e data sources',
    },
    {
      key: 'can_manage_workspace' as keyof WorkspacePermissions,
      label: 'Gerenciar Workspace',
      description: 'Gerenciar membros e grupos do workspace',
    },
    {
      key: 'can_admin' as keyof WorkspacePermissions,
      label: 'Administrador',
      description: 'TODAS as permissões',
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 w-screen h-screen bg-black/60 z-40"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl shadow-2xl p-6 z-50 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#2a2a2a] pb-4 z-10">
          <h2 className="text-xl font-semibold text-white">Editar Permissões</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Member Info */}
          <div className="p-3 bg-[#1a1a1a] border border-[#333333] rounded-lg">
            <p className="text-sm text-[#888888]">
              Editando permissões de{' '}
              <span className="font-medium text-white">{member.user_name || member.user?.name || member.user_email || member.user?.email || `User ${member.user_id}`}</span>
            </p>
          </div>

          {/* Permissions List */}
          <div className="space-y-3">
            {permissionList.map((perm) => (
              <label
                key={perm.key}
                className="flex items-start gap-3 p-3 bg-[#1a1a1a] border border-[#333333] rounded-lg cursor-pointer hover:border-[#444444] transition-colors"
              >
                <input
                  type="checkbox"
                  id={perm.key}
                  checked={permissions[perm.key] === 1}
                  onChange={() => handleToggle(perm.key)}
                  disabled={isSubmitting}
                  className="mt-1 h-4 w-4 rounded border-[#333333] bg-[#0a0a0a] text-[#F4CC04] focus:ring-[#F4CC04] focus:ring-offset-0 disabled:opacity-50"
                />
                <div className="flex-1">
                  <div className="font-medium text-white">{perm.label}</div>
                  <div className="text-sm text-[#888888]">{perm.description}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#333333]">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="
                px-4 py-2
                text-[#888888] hover:text-white
                hover:bg-[#333333]
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="
                px-4 py-2
                bg-[#F4CC04] hover:bg-[#D4B104]
                text-black font-medium
                rounded-lg transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default EditMemberPermissionsModal;
